import { useEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '@/lib/cn';
import { useIsDesktop } from '@/lib/hooks/useMediaQuery';
import { Wordmark } from '@/components/brand/Wordmark';
import { ThemeToggle } from './ThemeToggle';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { OfflineBanner } from '@/components/ui';
import { useSession } from '@/features/auth/sessionContext';
import { useCart } from '@/lib/api/cart';
import { paths } from '@/routes/paths';

export interface AppShellProps {
  /** Page title. Rendered as the single <h1> and set as document.title. */
  title: string;
  /** Sub-heading under the title. */
  subtitle?: ReactNode;
  /** Rendered above the title — normally a Stepper. */
  progress?: ReactNode;
  /** Back destination. Shown as a real link, so long-press/open-in-tab works. */
  backTo?: string;
  backLabel?: string;
  /**
   * The one primary action for this screen, pinned to the bottom of the viewport.
   * See StickyActionBar for why it lives there.
   */
  action?: ReactNode;
  /**
   * Desktop-only companion panel, shown in the summary rail above the action —
   * a running total, a document summary. Ignored below `lg`, where the same
   * information belongs inline in the flow and the screen is too narrow for a
   * second column.
   */
  aside?: ReactNode;
  children: ReactNode;
  /** Hides the h1 visually for screens that render their own hero. */
  titleHidden?: boolean;
  /**
   * Lets a page opt out of the reading-width cap and use the full shell width on
   * desktop — for the home page's hero, which is presentation rather than a form.
   */
  wide?: boolean;
}

/**
 * The customer-facing shell. Mobile-first, with a real desktop layout above `lg`.
 *
 * Layout decisions worth defending:
 *
 *   - THE FORM NEVER STRETCHES. Below `lg` it is one centred column at
 *     max-w-app (32rem); above `lg` it stays about as narrow and gains a second
 *     column beside it. A checkout form dragged across 1400px is harder to fill
 *     in, not easier — the eye loses the line, and a full-width text input looks
 *     like it wants an essay. The desktop win is not a wider form; it is having
 *     the price and the action visible AT THE SAME TIME as the choices.
 *   - ON DESKTOP THE ACTION MOVES INTO THE RAIL. A full-bleed bottom bar under a
 *     32rem column looks like a mistake, and on a mouse-driven screen there is no
 *     thumb to reach for. The action is rendered ONCE and re-parented (see
 *     useMediaQuery) rather than duplicated and hidden, so there are never two
 *     "Continue" buttons in the accessibility tree.
 *   - The primary action is pinned to the BOTTOM on touch, not the top or inline
 *     after the content. On a 6" phone held one-handed, the bottom third is the
 *     only comfortably reachable area, and "Continue" being the thing under your
 *     thumb is the difference between a two-tap step and a scroll-hunt.
 *   - No hover-only affordances anywhere: touch devices never fire hover, so
 *     hover styling is additive decoration only.
 *   - Route changes move focus to the <h1> and update document.title, because a
 *     client-side navigation otherwise leaves a screen reader silent and a keyboard
 *     user's focus stranded on the link they just followed.
 */
export function AppShell({
  title,
  subtitle,
  progress,
  backTo,
  backLabel = 'Back',
  action,
  aside,
  children,
  titleHidden = false,
  wide = false,
}: AppShellProps) {
  const location = useLocation();
  const isDesktop = useIsDesktop();
  // One node, one parent: the rail on desktop, the bottom bar on touch.
  const actionInRail = isDesktop && Boolean(action);
  const actionInBar = !isDesktop && Boolean(action);
  // The rail exists on desktop when it would hold anything at all.
  const hasRail = isDesktop && (Boolean(aside) || Boolean(action));
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const previousPath = useRef(location.pathname);

  useEffect(() => {
    document.title = `${title} · Printoman`;
  }, [title]);

  useEffect(() => {
    // Move focus to the new page's heading on navigation — a client-side route
    // change otherwise leaves a screen reader silent and a keyboard user's focus
    // stranded on the link they just followed.
    //
    // Keyed on a CHANGE of pathname rather than a "first render" flag: StrictMode
    // double-invokes effects in development, so a flag-based guard fires on the
    // initial mount anyway and steals focus on first paint (it also breaks the
    // skip link, which should be the first tab stop on a fresh load).
    if (previousPath.current === location.pathname) return;
    previousPath.current = location.pathname;
    headingRef.current?.focus();
  }, [location.pathname]);

  return (
    <div className="flex min-h-dvh flex-col bg-surface-muted">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <OfflineBanner />

      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex w-full max-w-shell items-center gap-2 px-4 py-3 lg:px-6">
          {backTo ? (
            <Link
              to={backTo}
              className="-ml-2 grid min-h-touch min-w-touch place-items-center rounded-control text-ink-muted hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
            >
              <span aria-hidden="true" className="text-xl">
                ‹
              </span>
              <span className="sr-only">{backLabel}</span>
            </Link>
          ) : null}
          <Link
            to="/"
            className="flex min-h-touch items-center gap-2 rounded-control px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
          >
            <Wordmark width={132} />
          </Link>

          {/* Pushed to the far end; the wordmark keeps the left. The cart
              comes first because it is the one people reach for mid-order,
              and the theme switch sits outermost because it is the one they
              touch once and then forget. */}
          <CartLink />
          <AccountLink />
          <ThemeToggle />
        </div>
      </header>

      <main
        id="main"
        className={cn(
          'mx-auto w-full flex-1 px-4 py-5 lg:px-6 lg:py-8',
          wide ? 'max-w-shell' : 'max-w-app lg:max-w-shell',
        )}
      >
        <div
          className={cn(
            'lg:grid lg:items-start lg:gap-8',
            // The rail only earns its column when there is something to put in
            // it. Otherwise the content keeps the full width.
            hasRail ? 'lg:grid-cols-[minmax(0,1fr)_20rem]' : '',
          )}
        >
          {/* Without a rail the column keeps its reading width and CENTRES, or
              it would sit against the left edge of a 64rem shell on a wide
              screen and look like a layout bug rather than a choice. */}
          <div className={cn('min-w-0', !hasRail && !wide ? 'lg:mx-auto lg:max-w-app' : '')}>
            {progress ? <div className="mb-5">{progress}</div> : null}

            <h1
              ref={headingRef}
              tabIndex={-1}
              className={cn(
                'text-2xl font-semibold tracking-tight text-ink focus-visible:outline-none lg:text-3xl',
                titleHidden && 'sr-only',
              )}
            >
              {title}
            </h1>
            {subtitle && !titleHidden ? <p className="mt-1.5 text-ink-muted">{subtitle}</p> : null}

            <div className={titleHidden ? '' : 'mt-5'}>{children}</div>
          </div>

          {hasRail ? (
            // `top-6` clears the header; `self-start` stops the grid stretching
            // the rail to the content's height, which would make sticky useless.
            <aside className="mt-6 hidden self-start lg:sticky lg:top-6 lg:mt-0 lg:block">
              <div className="flex flex-col gap-4">
                {aside}
                {actionInRail ? action : null}
              </div>
            </aside>
          ) : null}
        </div>
      </main>

      {/* The footer sits ABOVE the sticky bar in the DOM, not below it. Below,
          the bar would lose its stickiness the moment the footer scrolled into
          view and the primary action would slide off the bottom of a checkout
          step. Above, it stays pinned for the whole page.

          No bottom padding to clear the bar, and none is needed: `sticky` keeps
          the bar in the layout, so at full scroll it sits in its own flow
          position below the footer rather than over it. The reserve that used to
          be on <main> was for when main was the last element; with the footer
          there it only produced a 92px void above the action bar. */}
      <SiteFooter />

      {actionInBar ? <StickyActionBar>{action}</StickyActionBar> : null}
    </div>
  );
}

/**
 * The cart icon, top right on every customer page.
 *
 * It asks the SERVER what is in the cart rather than reading `?q=` — this
 * renders on the home page and on a policy page, neither of which has an id
 * to read, and the cart is found from the session anyway.
 *
 * NOT UNTIL THE SESSION HAS SETTLED. The server answers "whose cart is this?"
 * from the access token when there is one and from the anonymous cookie
 * otherwise, and the token is not there on the first render — the provider
 * has to redeem the refresh cookie for it first. Asking in that window asks
 * as a stranger, so a signed-in customer's header would read empty, and
 * because the endpoint creates a cart when it finds none the wrong answer
 * would be written down for every visitor who ever loaded a policy page.
 *
 * Always rendered, empty or not, signed in or not. An icon that appears only
 * once you have something in the cart is an icon nobody learns is there, and
 * the count is the only thing a customer has to tell them their second upload
 * landed in the same order as their first.
 */
function CartLink() {
  const session = useSession();
  const cart = useCart({ enabled: session.status !== 'loading' });
  // `?? []` rather than trusting the shape: this renders inside the header of
  // every page, so a truncated or unexpected response here would blank the
  // whole app rather than just the count.
  const count = (cart.data?.items ?? []).length;
  const label =
    count === 0
      ? 'Your cart is empty'
      : `Your cart, ${count === 1 ? '1 document' : `${count} documents`}`;

  return (
    <Link
      to={paths.cart}
      aria-label={label}
      title={label}
      className="relative ml-auto grid min-h-touch min-w-touch place-items-center rounded-control text-ink-muted hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 4h2l2.2 10.4a1.5 1.5 0 0 0 1.47 1.1h7.8a1.5 1.5 0 0 0 1.47-1.16L19.5 8H6" />
        <circle cx="9.5" cy="19" r="1.25" />
        <circle cx="16.5" cy="19" r="1.25" />
      </svg>
      {count > 0 ? (
        // The count is already in the link's accessible name, so the badge is
        // decorative — announcing "2" twice is worse than not announcing it.
        <span
          aria-hidden="true"
          className="absolute right-0.5 top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-xs font-semibold tabular-nums text-brand-contrast ring-1 ring-inset ring-brand-edge"
        >
          {count}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * The account icon, top right on every customer page.
 *
 * Always rendered, signed in or not. An icon that appears only once you have a
 * session is an icon nobody discovers, because the way in is through it. When
 * anonymous it still points at /account, which bounces to sign-in and returns
 * here afterwards — so the same tap means "my stuff" either way.
 *
 * A person glyph rather than the customer's initial: there is no name until
 * they give one, and an avatar with a "?" in it reads as an error.
 */
function AccountLink() {
  const { status } = useSession();
  const label = status === 'authenticated' ? 'Your account' : 'Sign in to your account';

  return (
    <Link
      to={paths.account}
      aria-label={label}
      title={label}
      className="grid min-h-touch min-w-touch place-items-center rounded-control text-ink-muted hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
    >
      {/* Inline SVG, not an icon font or a CDN sprite: one glyph is not worth a
          dependency, and it inherits currentColor so the theme handles it. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="8" r="3.25" />
        <path d="M4.5 19.5a7.5 7.5 0 0 1 15 0" />
      </svg>
    </Link>
  );
}

export interface StickyActionBarProps {
  children: ReactNode;
  /** Secondary text above the action, e.g. the running total. */
  summary?: ReactNode;
}

/**
 * The thumb-reachable primary action bar.
 *
 * `sticky bottom-0` inside a flex column rather than `fixed`: sticky participates
 * in layout, so it cannot end up floating over the footer on a short page, and it
 * does not need a body padding hack. `pb-safe` keeps it clear of the iOS home bar,
 * where a fixed button is physically hard to tap.
 */
export function StickyActionBar({ children, summary }: StickyActionBarProps) {
  return (
    <div
      data-testid="sticky-action-bar"
      className="sticky bottom-0 z-30 border-t border-line bg-surface/95 shadow-sticky backdrop-blur"
    >
      <div className="mx-auto flex max-w-app flex-col gap-2 px-4 pt-3 pb-safe">
        {summary ? <div className="text-sm text-ink-muted">{summary}</div> : null}
        {children}
      </div>
    </div>
  );
}
